import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { fmtBytes, fmtDate, fmtDuration } from "@/lib/api";
import { PageHeader, LogicalBackupNotice } from "@/components/Layout";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Server, HardDrive, CalendarClock, Activity, AlertTriangle, Stethoscope } from "lucide-react";

function Stat({ label, value, sub, icon: Icon, testid }) {
  return (
    <div className="grid-panel p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">{label}</p>
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
      </div>
      <p className="font-display font-black text-4xl tracking-tight mt-3">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

export default function Dashboard() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [testing, setTesting] = useState(false);

  const load = () => api.get("/dashboard").then((r) => setData(r.data)).catch((e) =>
    setErr(e?.response?.data?.detail || t("dashboard.loadFailed")));

  useEffect(() => { load(); }, []);

  const testAll = async () => {
    setTesting(true);
    try {
      const r = await api.post("/maintenance/test-all");
      const d = r.data;
      toast.success(t("dashboard.sweepResult", {
        sok: d.sources_ok, stotal: d.sources_ok + d.sources_failed,
        dok: d.destinations_ok, dtotal: d.destinations_ok + d.destinations_failed,
      }));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("dashboard.sweepFailed")); }
    finally { setTesting(false); }
  };

  if (err) return <div className="p-8 text-destructive" data-testid="dashboard-error">{err}</div>;
  if (!data) return <div className="p-8 font-mono text-sm text-muted-foreground spinner-dots">{t("common.loading")}</div>;

  const m = data.metrics || {};
  return (
    <div data-testid="dashboard-page">
      <PageHeader title={t("dashboard.title")} subtitle={t("dashboard.subtitle")}
        actions={
          <Button onClick={testAll} disabled={testing} className="rounded-none uppercase font-semibold bg-[#0A0A0A] hover:bg-[#0052FF]" data-testid="test-all-button">
            <Stethoscope className="h-4 w-4 mr-1.5" /> {testing ? t("common.testing") : t("dashboard.testAll")}
          </Button>
        } />
      <div className="p-6 md:p-8 space-y-6">
        <LogicalBackupNotice />

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <Stat label={t("dashboard.statRuns")} value={m.runs_total ?? 0}
            sub={t("dashboard.statRunsSub", { ok: m.runs_succeeded ?? 0, failed: m.runs_failed ?? 0 })} icon={Activity} testid="stat-runs" />
          <Stat label={t("dashboard.statActive")} value={m.runs_running ?? 0}
            sub={t("dashboard.statActiveSub", { queued: m.runs_queued ?? 0 })} icon={Activity} testid="stat-active" />
          <Stat label={t("dashboard.statStorage")} value={fmtBytes(m.total_storage_bytes)}
            sub={t("dashboard.statStorageSub")} icon={HardDrive} testid="stat-storage" />
          <Stat label={t("dashboard.statRestores")} value={m.restore_tests_total ?? 0}
            sub={t("dashboard.statRestoresSub", { passed: m.restore_tests_succeeded ?? 0 })} icon={Server} testid="stat-restores" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="grid-panel lg:col-span-2">
            <div className="px-5 py-3 border-b border-border flex items-center justify-between">
              <h3 className="font-display font-bold">{t("dashboard.recentRuns")}</h3>
              <Link to="/runs" className="font-mono text-xs text-brand hover:underline" data-testid="view-all-runs">{t("dashboard.viewAll")}</Link>
            </div>
            {data.recent_runs?.length ? (
              <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[560px]">
                <thead>
                  <tr className="text-left font-mono text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="px-5 py-2 font-medium">{t("dashboard.thPlan")}</th>
                    <th className="px-5 py-2 font-medium">{t("dashboard.thStatus")}</th>
                    <th className="px-5 py-2 font-medium">{t("dashboard.thSize")}</th>
                    <th className="px-5 py-2 font-medium">{t("dashboard.thWhen")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_runs.map((r) => (
                    <tr key={r.id} className="data-grid-row" data-testid={`recent-run-${r.id}`}>
                      <td className="px-5 py-2.5">
                        <Link to={`/runs/${r.id}`} className="hover:text-brand font-medium">{r.plan_name || r.plan_id}</Link>
                      </td>
                      <td className="px-5 py-2.5"><StatusBadge status={r.status} /></td>
                      <td className="px-5 py-2.5 font-mono text-xs">{fmtBytes(r.final_size)}</td>
                      <td className="px-5 py-2.5 font-mono text-xs text-muted-foreground">{fmtDate(r.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            ) : (
              <div className="px-5 py-12 text-center text-sm text-muted-foreground">{t("dashboard.noRuns")}</div>
            )}
          </div>

          <div className="space-y-6">
            <div className="grid-panel">
              <div className="px-5 py-3 border-b border-border flex items-center gap-2">
                <CalendarClock className="h-4 w-4" />
                <h3 className="font-display font-bold">{t("dashboard.upcoming")}</h3>
              </div>
              {data.upcoming_runs?.length ? (
                <ul className="divide-y divide-border">
                  {data.upcoming_runs.map((u, i) => (
                    <li key={i} className="px-5 py-2.5 flex items-center justify-between text-sm">
                      <span className="font-medium truncate">{u.plan_name}</span>
                      <span className="font-mono text-xs text-muted-foreground">{fmtDate(u.next_run)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="px-5 py-8 text-center text-sm text-muted-foreground">{t("dashboard.noScheduled")}</div>
              )}
            </div>

            <div className="grid-panel border-destructive/40">
              <div className="px-5 py-3 border-b border-border flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-destructive" />
                <h3 className="font-display font-bold">{t("dashboard.attention")}</h3>
              </div>
              <div className="px-5 py-3 space-y-2 text-sm">
                {(data.failing_sources?.length || data.failing_destinations?.length) ? (
                  <>
                    {data.failing_sources?.map((s) => (
                      <div key={s.id} className="flex items-center justify-between">
                        <span>{t("dashboard.sourcePrefix")} {s.name}</span><StatusBadge status="invalid" />
                      </div>
                    ))}
                    {data.failing_destinations?.map((d) => (
                      <div key={d.id} className="flex items-center justify-between">
                        <span>{t("dashboard.destPrefix")} {d.name}</span><StatusBadge status="unhealthy" />
                      </div>
                    ))}
                  </>
                ) : (
                  <p className="text-muted-foreground text-center py-4">{t("dashboard.allHealthy")}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
